import Axios, { AxiosRequestConfig } from 'axios';
import { RemoteFlashActionNames } from './types';
import { CompleteUpload, RegisterArguments } from '../../../services/types';
import RemoteFlash from '../../../services/remote-flash';
import { AppState, AppThunkDispatch } from '../..';
import ConfigurationFilesLibrary from '../../../services/config-files-library';
import { enqueueSnackbarAction } from '../../snackbar/actions';
import { QueueFirmwareRequest } from '../../../entities/remote-flash-v1/models';
import { getSoftwareStatusByDeviceKey } from '../actions';
import { getCommandsByDeviceKey } from '../../commands/actions';
import FileStorage from '../../../services/file-storage';
import { createChunks, parametersAsObject } from '../../../globals/utils';
import { waitForSuccessfulQueueThenMessage } from '../../commands/general-commands/actions';

export const queueSoftwareRemoteFlashAction =
  (input: QueueFirmwareRequest) => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
      search: { selectedEntity },
    } = getState();
    const deviceKey = selectedEntity?.deviceKey;
    if (selectedEntity && deviceKey) {
      const remoteFlashService = new RemoteFlash(client, envVariables.remoteFlashV1BaseUrl);
      const response = await remoteFlashService.queueFirmware(input);
      if (response.status === 200) {
        dispatch(
          enqueueSnackbarAction({
            message: 'Request Sent',
            options: { variant: 'info' },
          }),
        );
        dispatch({
          type: RemoteFlashActionNames.SET_FIRMWARE_STATUS,
          payload: response.data?.firmware,
        });
        dispatch(getSoftwareStatusByDeviceKey({ deviceKey }));
        const transactionId = response.data?.transactionId;
        dispatch(waitForSuccessfulQueueThenMessage(transactionId, deviceKey));
        return response;
      }
      dispatch(
        enqueueSnackbarAction({
          message: 'There was a problem sending your request',
          options: { variant: 'error' },
        }),
      );
      return null;
    }
    return null;
  };

export const queueExistingFile = ({ hash, transactionId, userId }
  : {
    hash: string,
    transactionId: string,
    userId: string
  }) =>
  async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      search: { selectedEntity },
      login: {
        client,
        envVariables: {
          remoteFlashV1BaseUrl,
        },
      },
    } = getState();
    const deviceKey = selectedEntity?.deviceKey;
    const deviceId = selectedEntity?.deviceId;
    if (deviceKey && deviceId) {
      const queueSoftwareParameters: QueueFirmwareRequest = {
        devices: [
          {
            deviceKey,
            tags: ['deploymentfile'],
          },
          {
            deviceId,
            tags: ['deploymentfile'],
          },
        ],
        hash,
        userId,
        transactionId,
        applicationId: 'iot-gateway',
      };

      const remoteFlashService = new RemoteFlash(client, remoteFlashV1BaseUrl);
      const remoteFlashResponse = await remoteFlashService.queueFirmware(
        queueSoftwareParameters,
      );
      const flashTransactionId = remoteFlashResponse.data?.transactionId;
      dispatch(waitForSuccessfulQueueThenMessage(flashTransactionId, deviceKey));
      dispatch(getSoftwareStatusByDeviceKey({ deviceKey }));
      return remoteFlashResponse;
    }
    return null;
  };

export const uploadBatchSoftwareConfigFile =
  ({
    partNumber,
    file,
    fileTypeId,
    setPercentUpload,
    overwrite,
    isRetrievable,
    uiStatusCode,
    hashId,
    transactionNewId,
    userId,
    overwriteCallback = () => { },
  }: {
    partNumber: string | undefined;
    file: File;
    fileTypeId: string;
    setPercentUpload: Function;
    overwrite?: boolean;
    isRetrievable?: boolean;
    uiStatusCode?: any;
    hashId?: any;
    transactionNewId?: any;
    userId?: any;
    overwriteCallback?: Function;
  }) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        search: { selectedEntity },
        login: {
          client,
          envVariables: {
            storageV1BaseUrl,
            configurationFilesLibraryV1BaseUrl,
          },
        },
      } = getState();
      if (selectedEntity) {
        const clientAuth: any = client?.authenticate();
        const userId = clientAuth.loginId;
        const storage = new FileStorage(client, storageV1BaseUrl);
        const chunks = createChunks(file, 10);
        const numberOfParts = chunks.length;
        const partyNumber = 'CAT';

        const deviceKey = selectedEntity?.deviceKey;
        const deviceId = selectedEntity?.deviceId;
        const deviceType = selectedEntity?.deviceType;
        if (deviceKey && deviceId && deviceType && selectedEntity) {
          const registrationInputs: RegisterArguments = {
            queryParameters: {
              partNumber,
              partyNumber,
              numberOfParts,
              fileName: file.name,
              origin: 'iot-gateway',
              ttl: 86400,
              contentType: 'application/bin',
            },
          };
          let transactionId;
          try {
            if (uiStatusCode && uiStatusCode === 409) {
              const remoteFlashResponse = await dispatch(queueExistingFile({ hash: hashId, transactionId: transactionNewId, userId }));
              setPercentUpload(100);
              if (remoteFlashResponse?.status === 200 || remoteFlashResponse?.status === 201) {
                setPercentUpload(0);
                dispatch({
                  type: RemoteFlashActionNames.SET_FILE_EXISTS_IN_DB,
                  payload: false,
                });
              }
            } else {
              let percentUpload = 10;
              setPercentUpload(percentUpload);
              const {
                data: { uploadUrls, id, uploadId },
              } = await storage.multiPartRegister(registrationInputs);

              const uploadMultiple = () =>
                uploadUrls.map(async (uploadUrl, iterator) => {
                  const uploadPercentage = 80 / uploadUrls.length;
                  const url = uploadUrl.split('?');
                  const parameterString: URLSearchParams | any = new URLSearchParams(url[1]);
                  const urlParameters = parametersAsObject(parameterString);
                  const request: AxiosRequestConfig = {
                    url: url[0],
                    params: urlParameters,
                    method: 'PUT',
                    data: chunks[iterator],
                    headers: {
                      'Content-Type': 'application/octet-stream',
                    },
                  };
                  const uploadResponse = await Axios(request);
                  percentUpload += uploadPercentage;
                  setPercentUpload(percentUpload);
                  return uploadResponse;
                });
              await Promise.all(uploadMultiple());

              const completionInput: CompleteUpload = {
                queryParameters: {
                  partyNumber,
                  uploadId,
                  id,
                },
              };
              const {
                data,
                data: { fileName },
              } = await storage.multiPartCompleteUpload(completionInput);
              percentUpload += 10;
              setPercentUpload(percentUpload);
              transactionId = data.id;

              const parameters = new URLSearchParams();
              parameters.append('fileId', data.id);
              parameters.append(
                'metadata',
                JSON.stringify({
                  isRetrievable,
                  fileTypeId,
                  fileName,
                  userId,
                  applicationId: 'iot-gateway',
                  partNumber,
                }),
              );
              parameters.append('overwrite', `${overwrite}`);
              const configure = new ConfigurationFilesLibrary(
                client,
                configurationFilesLibraryV1BaseUrl,
              );
              const finish = await configure.uploadConfigFilesFromStorage(parameters);
              percentUpload += 10;

              const remoteFlashResponse = await dispatch(queueExistingFile({ hash: finish.data.id, transactionId, userId }));
              setPercentUpload(100);
              if (remoteFlashResponse?.status === 200 || remoteFlashResponse?.status === 201) {
                setPercentUpload(0);
                dispatch({
                  type: RemoteFlashActionNames.SET_FILE_EXISTS_IN_DB,
                  payload: false,
                });
              }
              return finish;
            }
          } catch (error: any) {
            setPercentUpload(0);
            let message = '';
            if (error.response.status === 409) {
              if (error.response.data.isRetrievable) {
                message = 'File already exists in the database';
              }
              return overwriteCallback({ uiStatusCode: 409, hashId: error.response.data.id, transactionNewId: error.response.data.transactionId, ...error.response.data });
            }
            if (error.response.status === 403) {
              message = 'Permission Denied';
            } else if (error.response.status === 500) {
              message = 'Internal server error';
            }
            if (message) {
              dispatch(
                enqueueSnackbarAction({
                  message,
                  options: {
                    variant: 'error',
                  },
                }),
              );
            }
            return null;
          }
        }
        return null;
      }
      return null;
    };

export const updateSoftwareMetadataAction =
  (input: any) => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
      search: { selectedEntity },
    } = getState();
    const deviceKey = selectedEntity?.deviceKey;
    if (selectedEntity && deviceKey) {
      const remoteFlashService = new RemoteFlash(client, envVariables.remoteFlashV1BaseUrl);
      const response = await remoteFlashService.updateFirmwareMetadata(deviceKey, input);
      if (response.status === 200) {
        dispatch(
          enqueueSnackbarAction({
            message: 'Software metadata updated',
            options: { variant: 'success' },
          }),
        );
        dispatch({
          type: RemoteFlashActionNames.SET_FIRMWARE_STATUS,
          payload: response.data?.firmware,
        });
        dispatch(getSoftwareStatusByDeviceKey({ deviceKey }));
        dispatch(getCommandsByDeviceKey({ deviceKey, statuses: '1' }, true));
      } else {
        dispatch(
          enqueueSnackbarAction({
            message: 'Software metadata update failed',
            options: { variant: 'error' },
          }),
        );
      }
    }
  };
